import { useBenefitsStore } from '@/stores/benefits'
import { useCampaignsStore } from '@/stores/campaigns'
import { useUserStore } from '@/stores/user'
import type { Benefit, Campaign, User } from '@/types'
import { Icons } from '@/utils/helper-objects'
import { extractDetailsFromReferralUrl } from '@/utils/utils'
import type { RouteRecordProps } from 'env'
import type {
  NavigationGuardNext,
  RouteLocation,
  RouteRecordRaw,
} from 'vue-router'

import { EntryFilterType } from '../views/MembersZone/MemberEntriesView.vue'
import AuthMiddleware from './middleware/auth'

export default [
  {
    path: '/member-zone',
    beforeEnter: [AuthMiddleware],
    children: [
      {
        path: '',
        name: 'members-zone',
        meta: {
          title: "Member's Zone",
          nav: {
            sidebar: {
              display: () => {
                return useUserStore().isLoggedIn
              },
              text: 'Dashboard',
              icon: Icons.Entries,
              category: 'members-zone',
              order: 0,
            },
          },
        },
        component: () =>
          import('../views/MembersZone/MembersZoneDashboardView.vue'),
        props() {
          return {
            user: useUserStore().user,
          }
        },
      },
      {
        path: 'refer',
        name: 'members-zone-refer',
        props(route: RouteRecordProps<{ user: User; campaign: Campaign }>) {
          return {
            user: route.meta.props.user,
            campaign: route.meta.props.campaign,
          }
        },
        meta: {
          title: 'Refer A Friend',
          nav: {
            sidebar: {
              display: () => {
                return useUserStore().isLoggedIn
              },
              text: 'Referral',
              icon: Icons.Gift,
              category: 'members-zone',
              order: 4,
            },
          },
        },
        component: () => import('../views/MembersZone/MemberReferView.vue'),
        async beforeEnter(
          to: RouteLocation,
          _from: RouteLocation,
          next: NavigationGuardNext
        ) {
          const campaignStore = useCampaignsStore()
          const user = useUserStore().user!
          const { referralId } = extractDetailsFromReferralUrl(
            user.referral_link
          )

          if (!referralId) {
            return next({ name: 'not-found' })
          }

          try {
            const campaign =
              await campaignStore.fetchCampaignByCampaignId(referralId)

            to.meta.props = {
              ...(to.meta.props ?? {}),
              user,
              campaign,
            }

            next()
          } catch {
            return next({ name: 'not-found' })
          }
        },
      },
      {
        path: 'details',
        name: 'members-zone-details',
        props() {
          return {
            user: useUserStore().user,
          }
        },
        meta: {
          title: 'My Profile',
          nav: {
            sidebar: {
              display: () => {
                return useUserStore().isLoggedIn
              },
              text: 'My Profile',
              icon: Icons.Profile,
              category: 'members-zone',
              order: 1,
            },
          },
        },
        component: () => import('../views/MembersZone/MemberDetailsView.vue'),
      },
      // {
      //   path: 'socials',
      //   name: 'members-zone-socials',
      //   meta: {
      //     title: 'Socials',
      //     nav: {
      //       sidebar: {
      //         display: () => {
      //           return useUserStore().isLoggedIn
      //         },
      //         text: 'Socials',
      //         icon: Icons.Socials,
      //         category: 'members-zone',
      //         order: 8,
      //       },
      //     },
      //   },
      //   component: () => import('../views/MembersZone/MemberSocialsView.vue'),
      // },
      {
        path: 'loyalty',
        name: 'members-zone-loyalty',
        props() {
          return {
            user: useUserStore().user,
          }
        },
        meta: {
          title: 'My Loyalty',
          nav: {
            sidebar: {
              display: () => {
                return useUserStore().isLoggedIn
              },
              text: 'Loyalty',
              icon: Icons.Ribbon,
              category: 'members-zone',
              order: 7,
            },
          },
        },
        component: () => import('../views/MembersZone/MemberLoyaltyView.vue'),
      },
      {
        path: 'transactions',
        name: 'members-zone-balance-history',
        meta: {
          title: 'My Transactions',
          nav: {
            sidebar: {
              display: () => {
                return useUserStore().isLoggedIn
              },
              text: 'My Transactions',
              icon: Icons.Balance,
              category: 'members-zone',
              order: 5,
            },
          },
        },
        component: () =>
          import('../views/MembersZone/MemberTransactionsView.vue'),
      },
      {
        path: 'withdrawals',
        name: 'members-zone-balance-withdrawal',
        meta: {
          title: 'Withdrawals',
        },
        component: () =>
          import('../views/MembersZone/MemberWithdrawalsView.vue'),
      },
      {
        path: 'claim',
        name: 'members-zone-prize-claim',
        meta: {
          title: 'My Prizes',
        },
        component: () =>
          import('../views/MembersZone/MemberPrizeFulfilmentView.vue'),
      },
      {
        path: 'entries',
        children: [
          {
            path: '',
            name: 'members-zone-entries',
            props(route) {
              return {
                entryFilter:
                  route.hash.slice(1) === EntryFilterType.InstantGames
                    ? EntryFilterType.InstantGames
                    : EntryFilterType.Subscriptions,
              }
            },
            meta: {
              title: 'My Entries',
              nav: {
                sidebar: {
                  display: () => {
                    return useUserStore().isLoggedIn
                  },
                  text: 'Entries',
                  icon: Icons.Entries,
                  category: 'members-zone',
                  order: 6,
                },
              },
            },
            component: () =>
              import('../views/MembersZone/MemberEntriesView.vue'),
          },
          {
            path: ':code',
            name: 'members-zone-entries-detail',
            meta: {
              title: 'My Entries',
            },
            props: true,
            component: () =>
              import('../views/MembersZone/MemberEntriesDetailView.vue'),
          },
        ],
      },
      {
        path: 'benefits',
        children: [
          {
            path: '',
            name: 'members-zone-benefits',
            props() {
              return {
                user: useUserStore().user,
              }
            },
            meta: {
              title: 'My Benefits',
              nav: {
                sidebar: {
                  display: () => {
                    return useUserStore().isLoggedIn
                  },
                  text: 'My Benefits',
                  icon: Icons.Ticket,
                  category: 'members-zone',
                  order: 3,
                },
              },
            },
            component: () =>
              import('../views/MembersZone/MemberBenefitsView.vue'),
          },
          {
            path: 'vouchers',
            name: 'members-zone-benefits-vouchers',
            props() {
              return {
                user: useUserStore().user,
              }
            },
            meta: {
              title: 'My Vouchers',
            },
            component: () =>
              import('../views/MembersZone/MemberBenefitsVouchersView.vue'),
          },
          {
            path: ':code/claim',
            name: 'members-zone-benefits-claim',
            props: (route: RouteRecordProps<{ benefit: Benefit }>) => {
              return {
                benefit: route.meta.props.benefit,
              }
            },
            component: () =>
              import('../views/MembersZone/MemberBenefitsClaimView.vue'),
            async beforeEnter(
              to: RouteLocation,
              _from: RouteLocation,
              next: NavigationGuardNext
            ) {
              const benefitStore = useBenefitsStore()
              const benefits = await benefitStore.fetchBenefits()
              const benefit = benefits.find(
                (benefit) => benefit.code === to.params.code
              )
              if (!benefit) {
                return next({ name: 'not-found' })
              }
              // If the benefit isn't in the users plan, they shouldn't have
              // access to the claim page
              if (!benefitStore.isAvailableInPlan(benefit)) {
                return next({ name: 'members-zone-benefits' })
              }

              to.meta.props = {
                ...(to.meta.props ?? {}),
                benefit,
              }
              next()
            },
          },
        ],
      },
    ],
  },
] as Readonly<RouteRecordRaw[]>
